
import axios from 'axios';
import { SecureStorage } from '../storage/SecureStorage';
import { API_BASE, CUSTOMER_REFRESH_ENDPOINT } from '../config';

// ---- In‑memory state to avoid reading the plugin on each request ----
let accessTokenMem: string | null = null;
let refreshTokenMem: string | null = null;

// Callback that your app can register to handle global logout events (e.g. redirect, clear state, etc.)
let onLogoutCb: (() => void) | null = null;
export function registerOnLogout(cb: () => void) { onLogoutCb = cb; }

// Helpers to set/restore/clear tokens.  These will be called from your
// login flow and app startup to keep the in‑memory state in sync with
// persisted storage.
export async function setAuthFromLogin(loginResponse: any) {
    const accessToken = loginResponse?.accessToken ?? null;
    const refreshToken = loginResponse?.refreshToken ?? null;
    const user = loginResponse?.user ?? (() => {
        const { accessToken: _accessToken, refreshToken: _refreshToken, ...rest } = loginResponse || {};
        return rest;
    })();

    if (!accessToken || !refreshToken) {
        throw new Error('Respuesta de autenticación inválida');
    }

    accessTokenMem = accessToken;
    refreshTokenMem = refreshToken;
    await SecureStorage.saveTokens(accessToken, refreshToken);
    await SecureStorage.saveUser(user);
}

export async function restoreAuthFromStorage(options?: { refreshSession?: boolean }) {
    const { access, refresh } = await SecureStorage.getTokens();
    const user = await SecureStorage.getUser();
    accessTokenMem = access;
    refreshTokenMem = refresh;
    if (options?.refreshSession && refresh) {
        const refreshedAccess = await refreshTokens();
        if (refreshedAccess) {
            return { access: refreshedAccess, refresh: refreshTokenMem, user };
        }
    }
    return { access, refresh, user };
}

export async function clearAuth() {
    accessTokenMem = null;
    refreshTokenMem = null;
    await SecureStorage.clear();
    if (onLogoutCb) onLogoutCb();
}

// ---- Axios base instance ----
const api = axios.create({
    baseURL: API_BASE,
    headers: { 'Content-Type': 'application/json' },
    // Do not use cookies for auth; tokens are sent via Authorization header
});

// ---- Request interceptor: attach Bearer access token ----
api.interceptors.request.use(
    (config: any) => {
        config.headers = config.headers || {};
        if (accessTokenMem) {
            config.headers.Authorization = `Bearer ${accessTokenMem}`;
        }
        return config;
    },
    (error: any) => Promise.reject(error)
);

// ---- Refresh management with subscription queue ----
let refreshing = false;
let subscribers: ((token: string | null) => void)[] = [];
function subscribeTokenRefresh(cb: (t: string | null) => void) {
    subscribers.push(cb);
}
function notifySubscribers(token: string | null) {
    subscribers.forEach(cb => cb(token));
    subscribers = [];
}

async function refreshTokens(): Promise<string | null> {
    // If refresh token is not in memory, attempt to load it from storage
    if (!refreshTokenMem) {
        const { refresh } = await SecureStorage.getTokens();
        refreshTokenMem = refresh;
    }
    if (!refreshTokenMem) return null;

    try {
        const resp = await axios.post(
            `${API_BASE}${CUSTOMER_REFRESH_ENDPOINT}`,
            {},
            { headers: { Authorization: `Bearer ${refreshTokenMem}` } }
        );
        const { accessToken, refreshToken } = (resp.data || {}) as any;
        if (!accessToken || !refreshToken) return null;
        accessTokenMem = accessToken;
        refreshTokenMem = refreshToken;
        await SecureStorage.saveTokens(accessToken, refreshToken);
        return accessToken;
    } catch {
        return null;
    }
}

// ---- Response interceptor: 401 → attempt refresh ----
api.interceptors.response.use(
    (res: any) => res,
    async (error: any) => {
        const status = error.response?.status;
        const original = error.config as any;
        if (status !== 401 || original?._retry) {
            return Promise.reject(error);
        }
        // Mark the request so we don't loop
        original._retry = true;

        if (refreshing) {
            // If a refresh is already in progress, queue this request
            return new Promise((resolve, reject) => {
                subscribeTokenRefresh((token) => {
                    if (!token) return reject(error);
                    original.headers = original.headers ?? {};
                    original.headers.Authorization = `Bearer ${token}`;
                    resolve(api(original));
                });
            });
        }

        refreshing = true;
        const newAccess = await refreshTokens();
        refreshing = false;
        notifySubscribers(newAccess);

        if (!newAccess) {
            await clearAuth();
            return Promise.reject(error);
        }
        // Retry the original request with the new token
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api(original);
    }
);

export default api;
